import type { TFunction } from "i18next";
import i18n from "../../../i18n";
import {
  HistoryOrder,
  HistoryOrderStatus,
  HistoryOrderType,
} from "../../../services/orders/history";

/* -------------------------------------------------------------------------- */
/*                              TYPE / STATUS META                            */
/* -------------------------------------------------------------------------- */

export interface TypeMeta {
  code: HistoryOrderType;
  label: string;
  icon: string;
  /** Знак суммы: "+" — приход, "-" — расход, "" — обмен. */
  direction: "in" | "out" | "swap";
}

export const TYPE_META: Record<HistoryOrderType, TypeMeta> = {
  DEPOSIT: {
    code: "DEPOSIT",
    label: "Пополнение",
    icon: "solar:round-arrow-down-bold-duotone",
    direction: "in",
  },
  WITHDRAW: {
    code: "WITHDRAW",
    label: "Вывод",
    icon: "solar:round-arrow-up-bold-duotone",
    direction: "out",
  },
  EXCHANGE: {
    code: "EXCHANGE",
    label: "Обмен",
    icon: "solar:refresh-square-bold-duotone",
    direction: "swap",
  },
  CARD_ISSUE: {
    code: "CARD_ISSUE",
    label: "Выпуск карты",
    icon: "solar:card-2-bold-duotone",
    direction: "out",
  },
  CARD_TOPUP: {
    code: "CARD_TOPUP",
    label: "Пополнение карты",
    icon: "solar:card-transfer-bold-duotone",
    direction: "out",
  },
  ADJUSTMENT: {
    code: "ADJUSTMENT",
    // Направление вычисляется на лету по наличию buy/sell полей.
    // Для label используем нейтральное «Корректировка баланса».
    label: "Корректировка баланса",
    icon: "solar:shield-plus-bold-duotone",
    direction: "in",
  },
  SBP_PAYOUT: {
    code: "SBP_PAYOUT",
    label: "Оплата по QR-коду",
    icon: "solar:qr-code-bold-duotone",
    direction: "out",
  },
};

export type StatusGroup = "success" | "processing" | "error";

export interface StatusMeta {
  code: HistoryOrderStatus;
  group: StatusGroup;
  label: string;
  color: string;
  bg: string;
}

export const STATUS_META: Record<HistoryOrderStatus, StatusMeta> = {
  SUCCEEDED: {
    code: "SUCCEEDED",
    group: "success",
    label: "Успешно",
    color: "#22C55E",
    bg: "rgba(34, 197, 94, 0.15)",
  },
  NEW: {
    code: "NEW",
    group: "processing",
    label: "В обработке",
    color: "#F59E0B",
    bg: "rgba(245, 158, 11, 0.15)",
  },
  PENDING: {
    code: "PENDING",
    group: "processing",
    label: "В обработке",
    color: "#F59E0B",
    bg: "rgba(245, 158, 11, 0.15)",
  },
  IN_PROGRESS: {
    code: "IN_PROGRESS",
    group: "processing",
    label: "В обработке",
    color: "#F59E0B",
    bg: "rgba(245, 158, 11, 0.15)",
  },
  CANCELLED: {
    code: "CANCELLED",
    group: "error",
    label: "Отменено",
    color: "#EF4444",
    bg: "rgba(239, 68, 68, 0.15)",
  },
  FAILED: {
    code: "FAILED",
    group: "error",
    label: "Ошибка",
    color: "#EF4444",
    bg: "rgba(239, 68, 68, 0.15)",
  },
};

/** Обратный маппинг: группа статуса → все статусы, попадающие в неё. */
export const STATUSES_BY_GROUP: Record<StatusGroup, HistoryOrderStatus[]> = {
  success: ["SUCCEEDED"],
  processing: ["NEW", "PENDING", "IN_PROGRESS"],
  error: ["CANCELLED", "FAILED"],
};

/* -------------------------------------------------------------------------- */
/*                              I18N HELPERS                                  */
/* -------------------------------------------------------------------------- */

/**
 * Локализованный label типа операции. Реэкспорт «на лету» — берём
 * последнее значение из i18n, чтобы при смене языка старые компоненты,
 * читающие из константы, обновлялись автоматически.
 */
export function getTypeLabel(type: HistoryOrderType, t?: TFunction): string {
  const trans = t ?? (i18n.t.bind(i18n) as TFunction);
  switch (type) {
    case "DEPOSIT":
      return trans("history:types.deposit");
    case "WITHDRAW":
      return trans("history:types.withdraw");
    case "EXCHANGE":
      return trans("history:types.exchange");
    case "CARD_ISSUE":
      return trans("history:types.card_issue");
    case "CARD_TOPUP":
      return trans("history:types.card_topup");
    case "ADJUSTMENT":
      return trans("history:types.adjustment");
    case "SBP_PAYOUT":
      return trans("history:types.sbp");
  }
  return TYPE_META[type as HistoryOrderType]?.label ?? String(type);
}

export function getStatusLabel(status: HistoryOrderStatus, t?: TFunction): string {
  const trans = t ?? (i18n.t.bind(i18n) as TFunction);
  switch (status) {
    case "SUCCEEDED":
      return trans("history:statuses.succeeded");
    case "NEW":
    case "PENDING":
    case "IN_PROGRESS":
      return trans("history:statuses.pending");
    case "CANCELLED":
      return trans("history:statuses.cancelled");
    case "FAILED":
      return trans("history:statuses.failed");
  }
  return STATUS_META[status as HistoryOrderStatus]?.label ?? String(status);
}

export function getShortStatusLabel(
  status: HistoryOrderStatus,
  t?: TFunction,
): string {
  const trans = t ?? (i18n.t.bind(i18n) as TFunction);
  const meta = STATUS_META[status];
  if (!meta) return "";
  if (meta.group === "success") return trans("history:statuses.short_success");
  if (meta.group === "error") return trans("history:statuses.short_error");
  return trans("history:statuses.short_processing");
}

/* -------------------------------------------------------------------------- */
/*                                 FORMATTING                                 */
/* -------------------------------------------------------------------------- */

function formatAmount(v: string | null, digits = 2): string {
  if (v === null || v === undefined || v === "") return "0";
  const num = Number(v);
  if (!Number.isFinite(num)) return "0";
  return num.toLocaleString("ru-RU", {
    maximumFractionDigits: digits,
    minimumFractionDigits: 0,
  });
}

export interface DisplayAmount {
  primary: string;
  primaryColor: string | null;
  secondary?: string;
}

/** Что показать справа от строки в списке (крупно + мелко). */
export function getRowAmount(o: HistoryOrder): DisplayAmount {
  const meta = TYPE_META[o.type];

  // ADJUSTMENT — направление определяется тем, какая пара полей заполнена
  // (см. бэкенд BalancesService.adjust/setExact).
  if (o.type === "ADJUSTMENT") {
    if (o.buy_currency && o.buy_amount) {
      return {
        primary: `+ ${formatAmount(o.buy_amount)} ${o.buy_currency}`.trim(),
        primaryColor: "#22C55E",
      };
    }
    if (o.sell_currency && o.sell_amount) {
      return {
        primary: `− ${formatAmount(o.sell_amount)} ${o.sell_currency}`.trim(),
        primaryColor: null,
      };
    }
    return { primary: "—", primaryColor: null };
  }

  if (meta.direction === "in") {
    const amount = o.sell_amount ?? o.buy_amount;
    const currency = o.sell_currency ?? o.buy_currency ?? "";
    return {
      primary: `+ ${formatAmount(amount)} ${currency}`.trim(),
      primaryColor: "#22C55E",
    };
  }

  // SBP-платёж: крупно — USDT (списанное с баланса), мелко — сумма чека в ₽.
  if (o.type === "SBP_PAYOUT") {
    return {
      primary: `− ${formatAmount(o.sell_amount)} ${o.sell_currency ?? "USDT"}`.trim(),
      primaryColor: null,
      secondary: o.buy_amount
        ? `${formatAmount(o.buy_amount)} ₽`
        : undefined,
    };
  }

  if (meta.direction === "out") {
    const amount = o.sell_amount ?? o.buy_amount;
    const currency = o.sell_currency ?? o.buy_currency ?? "";
    return {
      primary: `− ${formatAmount(amount)} ${currency}`.trim(),
      primaryColor: null,
    };
  }

  const sellStr = `− ${formatAmount(o.sell_amount)} ${o.sell_currency ?? ""}`.trim();
  const buyStr = `+ ${formatAmount(o.buy_amount)} ${o.buy_currency ?? ""}`.trim();
  return {
    primary: sellStr,
    primaryColor: null,
    secondary: buyStr,
  };
}

/** Подпись под названием (например, «На +7 961… » или «От 0x55Ad…D31285»). */
export function getRowSubtitle(o: HistoryOrder, t?: TFunction): string {
  const trans = t ?? (i18n.t.bind(i18n) as TFunction);
  // Ручная корректировка баланса — покажем причину как subtitle.
  if (o.type === "ADJUSTMENT") {
    return o.comment?.trim() || trans("history:subtitle.adjustment_default");
  }
  // SBP-платёж: address здесь — это получатель (напр. `БФПН "Алёша"`),
  // не адрес сети. Не сокращаем и не пишем «От/На» — просто имя получателя.
  if (o.type === "SBP_PAYOUT") {
    if (o.address) return o.address;
    return trans("history:subtitle.sbp");
  }
  if (o.address) {
    const short = shorten(o.address);
    return o.type === "WITHDRAW"
      ? trans("history:subtitle.to", { addr: short })
      : trans("history:subtitle.from", { addr: short });
  }
  if (o.external_tx_hash) {
    return trans("history:subtitle.from", { addr: shorten(o.external_tx_hash) });
  }
  if (o.network_code) return o.network_code;
  if (o.type === "EXCHANGE" && o.sell_currency && o.buy_currency) {
    return `${o.sell_currency} → ${o.buy_currency}`;
  }
  return "";
}

function shorten(s: string): string {
  if (s.length <= 14) return s;
  return `${s.slice(0, 6)}…${s.slice(-7)}`;
}

/* -------------------------------------------------------------------------- */
/*                                DATE GROUPING                               */
/* -------------------------------------------------------------------------- */

/**
 * Название группы для даты: «Сегодня», «Вчера», «7 июля» или
 * «7 July 2025». Учитывает текущий язык i18n — Intl.DateTimeFormat
 * даёт корректные названия месяцев для ru/en.
 */
export function formatGroupLabel(
  d: Date,
  now = new Date(),
  t?: TFunction,
): string {
  const trans = t ?? (i18n.t.bind(i18n) as TFunction);
  const startOfDay = (x: Date) => new Date(x.getFullYear(), x.getMonth(), x.getDate());
  const today = startOfDay(now).getTime();
  const target = startOfDay(d).getTime();
  const dayMs = 24 * 60 * 60 * 1000;

  if (target === today) return trans("history:groups.today");
  if (target === today - dayMs) return trans("history:groups.yesterday");
  const sameYear = d.getFullYear() === now.getFullYear();
  const locale = (i18n.resolvedLanguage || i18n.language || "en").slice(0, 2);
  const monthName = new Intl.DateTimeFormat(locale === "ru" ? "ru-RU" : "en-US", {
    month: "long",
  }).format(d);
  const base = locale === "ru"
    ? `${d.getDate()} ${monthName}`
    : `${monthName} ${d.getDate()}`;
  return sameYear ? base : `${base} ${d.getFullYear()}`;
}

export interface HistoryGroup {
  label: string;
  key: string;
  items: HistoryOrder[];
}

/** Группирует заявки по дню создания, сохраняя порядок (свежее сверху). */
export function groupByDay(items: HistoryOrder[]): HistoryGroup[] {
  const now = new Date();
  const buckets = new Map<string, HistoryGroup>();
  for (const o of items) {
    const d = new Date(o.created_at);
    if (Number.isNaN(d.getTime())) continue;
    const key = `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
    let g = buckets.get(key);
    if (!g) {
      g = { key, label: formatGroupLabel(d, now), items: [] };
      buckets.set(key, g);
    }
    g.items.push(o);
  }
  return Array.from(buckets.values());
}

/** Форматирует ISO-дату для деталей: «18 июл 2026 14:39» или «18 Jul 2026 14:39». */
export function formatDetailDate(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  const locale = (i18n.resolvedLanguage || i18n.language || "en").slice(0, 2);
  const monthShort = new Intl.DateTimeFormat(
    locale === "ru" ? "ru-RU" : "en-US",
    { month: "short" },
  )
    .format(d)
    .replace(".", "");
  const hh = String(d.getHours()).padStart(2, "0");
  const mm = String(d.getMinutes()).padStart(2, "0");
  return `${d.getDate()} ${monthShort} ${d.getFullYear()} ${hh}:${mm}`;
}

/** Хелпер для форматирования суммы в деталях (без знака). */
export function formatDetailAmount(v: string | null, digits = 2): string {
  return formatAmount(v, digits);
}

/* -------------------------------------------------------------------------- */
/*                                 EXPLORERS                                  */
/* -------------------------------------------------------------------------- */

const EXPLORER_URLS: Record<string, (hash: string) => string> = {
  TRC20: (h) => `https://tronscan.org/#/transaction/${h}`,
  TRON: (h) => `https://tronscan.org/#/transaction/${h}`,
  BEP20: (h) => `https://bscscan.com/tx/${h}`,
  BSC: (h) => `https://bscscan.com/tx/${h}`,
  ERC20: (h) => `https://etherscan.io/tx/${h}`,
  ETH: (h) => `https://etherscan.io/tx/${h}`,
  ETHEREUM: (h) => `https://etherscan.io/tx/${h}`,
};

/** URL блок-обозревателя для транзакции по коду сети и hash'у. */
export function getExplorerUrl(networkCode: string | null, hash: string | null): string | null {
  if (!networkCode || !hash) return null;
  const factory = EXPLORER_URLS[networkCode.toUpperCase()];
  return factory ? factory(hash) : null;
}
